"use client";
import {
	RiArrowDownSLine,
	RiCalendarCheckFill,
	RiCalendarCheckLine,
	RiCheckLine,
	RiCloseLine,
	RiLiveFill,
	RiLiveLine,
	RiSignpostFill,
	RiSignpostLine,
} from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { Spinner } from "@workspace/ui/components/spinner";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";
import { useRealtimeFestivalStatus } from "@/hooks/features/festival/use-realtime-festival-status";
import { useCurrentUserDisplay } from "@/hooks/shared/use-auth";
import {
	USER_FESTIVAL_STATUS,
	type UserFestivalStatus,
} from "@/lib/features/festival/user-festival-status";

interface FestivalStatusButtonProps {
	festivalId: string;
	currentStatus: UserFestivalStatus;
	festivalIsPast: boolean;
	className?: string;
	size?: "sm" | "default" | "lg";
	onStatusChange?: (newStatus: UserFestivalStatus) => void;
	onAuthRequired?: () => void;
}

/**
 * Festival Status Button with Real-time Updates
 *
 * This button uses Supabase Realtime for instant cross-tab/device synchronization.
 * No cache invalidation needed - database events drive all updates.
 */
export function FestivalStatusButton({
	festivalId,
	festivalIsPast,
	className,
	size = "default",
	onStatusChange,
	onAuthRequired,
}: FestivalStatusButtonProps) {
	// Get current user for real-time subscription
	const { data: currentUser } = useCurrentUserDisplay();

	// Real-time festival status - single source of truth
	const { status, updateStatus, isPending } = useRealtimeFestivalStatus({
		festivalId,
		userId: currentUser?.id || null,
		enabled: !!currentUser?.id,
	});

	const handleStatusChange = async (newStatus: UserFestivalStatus) => {
		// Skip if selecting the same status (except when removing)
		if (newStatus === status && newStatus !== null) return;

		// Update via real-time hook
		await updateStatus(newStatus);

		// Notify parent component if needed
		onStatusChange?.(newStatus);
	};

	// Get current button config based on real-time status
	const getButtonConfig = () => {
		if (!status) {
			return {
				label: festivalIsPast ? "Wanted to Go" : "Want to Go",
				icon: RiSignpostLine,
				variant: "secondary" as const,
			};
		}

		switch (status) {
			case USER_FESTIVAL_STATUS.WANT_TO_GO:
				return {
					label: festivalIsPast ? "Wanted to Go" : "Want to Go",
					icon: RiSignpostFill,
					variant: "default" as const,
				};
			case USER_FESTIVAL_STATUS.GOING:
				return {
					label: festivalIsPast ? "Went" : "Going",
					icon: RiCalendarCheckFill,
					variant: "default" as const,
				};
			case USER_FESTIVAL_STATUS.WENT:
				return {
					label: "Went",
					icon: RiCalendarCheckFill,
					variant: "default" as const,
				};
			case USER_FESTIVAL_STATUS.WATCHED_ONLINE:
				return {
					label: "Saw Stream",
					icon: RiLiveFill,
					variant: "default" as const,
				};
			default:
				return {
					label: festivalIsPast ? "Wanted to Go" : "Want to Go",
					icon: RiSignpostLine,
					variant: "secondary" as const,
				};
		}
	};

	const config = getButtonConfig();
	const Icon = config.icon;
	const isAuthenticated = !!currentUser?.id;

	// Handle click - open auth modal for non-auth users
	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (!isAuthenticated) {
			if (onAuthRequired) {
				onAuthRequired();
			} else {
				console.warn("FestivalStatusButton: onAuthRequired not provided for non-auth user");
			}
			return;
		}
		if (!status) {
			handleStatusChange(USER_FESTIVAL_STATUS.WANT_TO_GO);
		}
	};

	return (
		<div className={cn("flex", className)}>
			{/* Main button */}
			<Button
				variant={config.variant}
				size={size}
				onClick={handleClick}
				disabled={isAuthenticated && isPending}
				className={cn(
					"rounded-r-none",
					(status === USER_FESTIVAL_STATUS.GOING ||
						status === USER_FESTIVAL_STATUS.WENT) &&
						"bg-radix-green-9 hover:bg-radix-green-10 text-radix-green-1",
					status === USER_FESTIVAL_STATUS.WATCHED_ONLINE &&
						"bg-radix-ruby-9 hover:bg-radix-ruby-10 text-radix-ruby-1",
				)}
			>
				{isPending ? (
					<Spinner className="h-4 w-4 animate-spin" />
				) : (
					<Icon className="h-4 w-4" />
				)}
				{config.label}
			</Button>

			{/* Dropdown */}
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant={config.variant}
						size={size}
						className={cn(
							"rounded-l-none border-l-0 px-1",
							(status === USER_FESTIVAL_STATUS.GOING ||
								status === USER_FESTIVAL_STATUS.WENT) &&
								"bg-radix-green-9 hover:bg-radix-green-10 text-radix-green-1",
							status === USER_FESTIVAL_STATUS.WATCHED_ONLINE &&
								"bg-radix-ruby-9 hover:bg-radix-ruby-10 text-radix-ruby-1",
						)}
						disabled={isPending || !isAuthenticated}
					>
						<RiArrowDownSLine className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-48">
					{/* Want to Go / Wanted to Go */}
					<DropdownMenuItem
						onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						handleStatusChange(USER_FESTIVAL_STATUS.WANT_TO_GO);
					}}
					>
						<RiSignpostLine className="h-4 w-4" />
						{festivalIsPast ? "Wanted to Go" : "Want to Go"}
						{status === USER_FESTIVAL_STATUS.WANT_TO_GO && (
							<RiCheckLine className="ml-auto h-4 w-4" />
						)}
					</DropdownMenuItem>

					{/* Going */}
					{!festivalIsPast && (
						<DropdownMenuItem
							onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							handleStatusChange(USER_FESTIVAL_STATUS.GOING);
						}}
						>
							<RiCalendarCheckLine className="h-4 w-4" />
							Going
							{status === USER_FESTIVAL_STATUS.GOING && (
								<RiCheckLine className="ml-auto h-4 w-4" />
							)}
						</DropdownMenuItem>
					)}

					{/* Went */}
					{(festivalIsPast || status === USER_FESTIVAL_STATUS.WENT) && (
						<DropdownMenuItem
							onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							handleStatusChange(USER_FESTIVAL_STATUS.WENT);
						}}
						>
							<RiCalendarCheckLine className="h-4 w-4" />
							Went
							{status === USER_FESTIVAL_STATUS.WENT && (
								<RiCheckLine className="ml-auto h-4 w-4" />
							)}
						</DropdownMenuItem>
					)}

					{/* Saw Stream */}
					{festivalIsPast && (
						<DropdownMenuItem
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								handleStatusChange(USER_FESTIVAL_STATUS.WATCHED_ONLINE);
							}}
						>
							<RiLiveLine className="h-4 w-4" />
							Saw Stream
							{status === USER_FESTIVAL_STATUS.WATCHED_ONLINE && (
								<RiCheckLine className="ml-auto h-4 w-4" />
							)}
						</DropdownMenuItem>
					)}

					{/* Remove */}
					{status && (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								handleStatusChange(null);
							}}
								className="text-destructive"
							>
								<RiCloseLine className="h-4 w-4" />
								Remove Status
							</DropdownMenuItem>
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
